import { inject, Injectable } from '@angular/core';
import { StorageService } from '../services/storage.service';
import { ApiService } from '../services/api.service';
import { AuthStateService } from './auth/auth-state.service';
import { ShowRoleType } from '../models/show-role-type.model';
import { User } from '../models/user.model';
import { UserLiteDto } from './admin/shows/show-admin.model';
import { UserShowRole } from '../models/user-show-role.model';

@Injectable({
    providedIn: 'root'
})
export class ShowService {

    private storageService: StorageService = inject(StorageService);
    private authStateService: AuthStateService = inject(AuthStateService);
    private apiService: ApiService = inject(ApiService);

    constructor() {}

    public async setActiveShowId(showId: number): Promise<void> {
        await this.storageService.setItem('showId', showId);
    }

    /**
     * Gets the active show ID from AuthStateService.
     * This is the single source of truth, set by loadCurrentUser() during authentication.
     * No fallback to storage - stale storage values caused incorrect API calls.
     */
    public async getActiveShowId(): Promise<number | null> {
        return this.authStateService.activeShowId;
    }

    // ========== Plannable Roles Operations ==========

    /**
     * Gets plannable role types for a show.
     * This is for the planner, not admin - uses the non-admin endpoint.
     */
    public async getPlannableRoleTypes(showId: number): Promise<ShowRoleType[]> {
        return await this.apiService.get<ShowRoleType[]>(`shows/${showId}/role-types/plannable`);
    }

    /**
     * Gets plannable role types for multiple shows.
     * Merges role types from all shows, deduplicating by name.
     */
    public async getPlannableRoleTypesForShows(showIds: number[]): Promise<ShowRoleType[]> {
        if (showIds.length === 0) {
            return [];
        }

        const roleTypeArrays = await Promise.all(
            showIds.map(showId => this.getPlannableRoleTypes(showId))
        );

        // Merge and deduplicate by name (keep first occurrence)
        const seenNames = new Set<string>();
        const mergedRoleTypes: ShowRoleType[] = [];

        for (const roleTypes of roleTypeArrays) {
            for (const roleType of roleTypes) {
                if (!seenNames.has(roleType.name)) {
                    seenNames.add(roleType.name);
                    mergedRoleTypes.push(roleType);
                }
            }
        }

        return mergedRoleTypes.sort((a, b) => a.displayOrder - b.displayOrder);
    }

    /**
     * Gets users who have plannable roles for the given shows.
     */
    public async getPlannableUsersForShows(showIds: number[]): Promise<User[]> {
        if (showIds.length === 0) {
            return [];
        }

        const showIdsParam = showIds.join(',');
        return await this.apiService.get<User[]>(`shows/plannable-users?showIds=${showIdsParam}`);
    }

    // ========== Crew Data Operations (for showreport) ==========

    /**
     * Gets all role types for a show.
     * Uses non-admin endpoint accessible to all authenticated users with show access.
     */
    public async getRoleTypes(showId: number): Promise<ShowRoleType[]> {
        return await this.apiService.get<ShowRoleType[]>(`shows/${showId}/role-types`);
    }

    /**
     * Gets users linked to a show.
     * Uses non-admin endpoint accessible to all authenticated users with show access.
     */
    public async getUsersLinkedToShow(showId: number): Promise<UserLiteDto[]> {
        return await this.apiService.get<UserLiteDto[]>(`shows/${showId}/users`);
    }

    /**
     * Gets all user role assignments for a show.
     * Returns which users have which roles for crew selection.
     */
    public async getUserRoleAssignments(showId: number): Promise<UserShowRole[]> {
        return await this.apiService.get<UserShowRole[]>(`shows/${showId}/user-roles`);
    }

}
